import { useEffect, useState, type ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../../utils/helpers';
import { t } from '../../i18n';

export function FilterBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3 border-b border-secondary-100 p-4 sm:flex-row sm:flex-wrap sm:items-end', className)}>
      {children}
    </div>
  );
}

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/** Text search that reports changes 300 ms after typing stops. */
export function SearchInput({ value, onChange, placeholder = t('Hľadať…'), className }: SearchInputProps) {
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => onChange(draft), 300);
    return () => window.clearTimeout(timer);
  }, [draft, value, onChange]);

  return (
    <div className={cn('relative w-full sm:w-72', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
      <input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full rounded-lg border border-secondary-300 bg-white py-2 pl-9 pr-8 text-sm placeholder:text-secondary-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 [&::-webkit-search-cancel-button]:hidden"
      />
      {draft && (
        <button
          type="button"
          onClick={() => {
            setDraft('');
            onChange('');
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-secondary-400 hover:text-secondary-700"
          aria-label={t('Vymazať hľadanie')}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
